import { CommonModule } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  DOCUMENT,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { BlobPipe, ClientInfo, fromResizeObserver } from 'wflow-cdk';
import {
  WfNavigationComponent,
  WfNavigationMobileComponent,
  WfNavigationFacadeService,
} from 'wflow-features/navigation';
import { TopBannerSlotsService } from 'wflow-main-app/src/app/core/components/top-banner/top-banner-slots.service';
import { TopNavigationComponent } from 'wflow-main-app/src/app/core/components/top-navigation/top-navigation.component';
import { TopNavigationSlotDirective } from 'wflow-main-app/src/app/core/components/top-navigation/top-navigation-slot.directive';
import { IconsModule, WuiButtonModule, WuiTooltipModule } from 'wflow-ui';
import { OuterWindowControlService } from '../../services/outer-window/outer-window-control.service';
import { ContextSelectorComponent } from './context-selector/context-selector.component';
import { ContextNavigationSlotService } from './page-container.directive';
import { PageContainerService } from './page-container.service';

@Component({
  selector: 'page-container',
  imports: [
    CommonModule,
    WuiButtonModule,
    IconsModule,
    RouterModule,
    WuiTooltipModule,
    TranslateModule,
    ContextSelectorComponent,
    TopNavigationComponent,
    TopNavigationSlotDirective,
    WfNavigationComponent,
    WfNavigationMobileComponent,
    BlobPipe,
  ],
  templateUrl: './page-container.component.html',
  styleUrls: ['./page-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'page-container',
    '[class.page-container--external]': 'isExternalWindow',
    '[class.page-container--mobile]': 'isMobile()',
  },
})
export class PageContainerComponent {
  private readonly clientInfo = inject(ClientInfo);
  protected readonly slotSvc = inject(ContextNavigationSlotService);
  protected readonly topBannerSvc = inject(TopBannerSlotsService);
  protected readonly pageContainerSvc = inject(PageContainerService);
  private readonly outerWindowControlSvc = inject(OuterWindowControlService);
  private readonly navigationFacadeSvc = inject(WfNavigationFacadeService);
  private readonly document = inject(DOCUMENT);

  readonly isMobile = this.clientInfo.isMobile;
  readonly hideNavigation = input(false, {
    transform: booleanAttribute,
  });
  readonly showWorkspaceImmediately = input(false, {
    transform: booleanAttribute,
  });
  readonly hideWorkspaceImmediately = input(false, {
    transform: booleanAttribute,
  });

  readonly navSize = signal(0);
  readonly paddingSize = computed(() => {
    if (this.hideNavigation()) return 0;
    const desktopNavHidden = this.isMobile() && this.showWorkspaceImmediately();
    return desktopNavHidden ? 0 : this.navSize();
  });
  readonly navSlot = this.slotSvc.navSlot;
  readonly workspaceSlot = this.slotSvc.workspaceSlot;
  readonly isExternalWindow = this.outerWindowControlSvc.isExternalWindow;

  readonly mobileNav = viewChild(WfNavigationMobileComponent);
  private readonly topBannerContainer =
    viewChild<ElementRef<HTMLElement>>('topBannerContainer');
  readonly logo$ = this.navigationFacadeSvc.logo$;
  readonly hasBadges = computed(() => {
    const badges = this.navigationFacadeSvc.badges.badges();
    return !!badges && Object.values(badges).some((count) => (count || 0) > 0);
  });

  constructor() {
    effect((onCleanup) => {
      const banner = this.topBannerContainer()?.nativeElement;
      if (!banner) {
        return;
      }

      const rootStyle = this.document.documentElement.style;
      const subscription = fromResizeObserver(banner).subscribe((rect) => {
        rootStyle.setProperty('--wf-top-banner-height', `${rect.height}px`);
      });

      onCleanup(() => {
        subscription.unsubscribe();
        rootStyle.removeProperty('--wf-top-banner-height');
      });
    });
  }

  openMobileMenu(): void {
    this.mobileNav()?.openMenu();
  }

  private inited = false;
  onNavInit() {
    if (this.inited) {
      return;
    }

    this.inited = true;
  }

  onContextSelectorMenuOpen() {
    this.navigationFacadeSvc.barAnimation.freeze();
  }

  onContextSelectorMenuClose() {
    this.navigationFacadeSvc.barAnimation.unfreeze();
  }
}
